/**
 * page-routes.js — the ONE place a page's URL is decided (BC-3, F3).
 *
 * The flat exporter owned these functions privately, so every surface that let
 * a user pick "a page" as a link destination re-derived the URL on its own:
 *
 *  - the navigation link editor wrote `name.toLowerCase().replace(/\s+/g,'-')
 *    + '.html'`, ignoring `page.slug` (composed multipage states carry the
 *    source template's slug), keeping punctuation, and naming the home page
 *    `home.html` instead of the site root;
 *  - the link popover kept its own slugify copy, also name-only.
 *
 * Choosing a page must write the route the EXPORTERS resolve. The functions
 * below are the flat exporter's, moved verbatim (the golden export test pins
 * that nothing changed), plus `pageHref`, which produces the form every
 * exporter and the preview router already accept: `/` for the first page and
 * `/<slug>/` for the rest. The flat exporter rewrites that to `<slug>.html`
 * (`pageForInternalHref`), 11ty/Astro match it against their routing table,
 * and the canvas preview resolves it by path.
 *
 * Pure, no DOM, no store.
 */

/**
 * The slug a page is exported under, before collision handling.
 * Prefers the page's stored slug over its name.
 */
export function normalizedPageSlug(page, fallback = '') {
  const value = String(page?.slug || page?.name || fallback)
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .replace(/\.html?$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return value;
}

/**
 * Determine the HTML filename for a page in a flat-file export.
 * First page → index.html, others → <slug>.html
 */
export function pageFileName(page, allPages) {
  const idx = allPages.findIndex((p) => p.id === page.id);
  if (idx === 0) return 'index.html';
  const slug = normalizedPageSlug(page, `page-${idx + 1}`) || `page-${idx + 1}`;
  return `${slug}.html`;
}

/** The page an internal href points at, or null for external/anchor/unknown. */
export function pageForInternalHref(href, allPages) {
  const value = String(href || '').trim();
  if (
    !value ||
    value.startsWith('#') ||
    value.startsWith('?') ||
    value.startsWith('//') ||
    /^[a-z][a-z0-9+.-]*:/i.test(value)
  )
    return null;
  const path = value
    .split(/[?#]/, 1)[0]
    .replace(/^(?:\.\.\/)+|^\.\//, '')
    .replace(/^\/+|\/+$/g, '')
    .replace(/\.html?$/i, '')
    .toLowerCase();
  if (!path || path === 'index' || path === 'home') return allPages[0] || null;
  return (
    allPages.find((page, index) => {
      const slug = normalizedPageSlug(page, `page-${index + 1}`);
      const name = normalizedPageSlug({ name: page.name });
      return path === slug || path.endsWith(`/${slug}`) || path === name;
    }) || null
  );
}

/**
 * The href to WRITE when a user picks a page as a link destination.
 * `/` for the first page, `/<slug>/` for the rest — the one form every
 * exporter and the preview router resolve back to the same page.
 */
export function pageHref(page, allPages) {
  const idx = allPages.findIndex((p) => p.id === page.id);
  if (idx <= 0) return '/';
  const slug = normalizedPageSlug(page, `page-${idx + 1}`) || `page-${idx + 1}`;
  return `/${slug}/`;
}

/**
 * Link-destination choices for a page picker: one entry per page, labelled,
 * with the href `pageHref` writes.
 * @returns {{id: string, label: string, href: string}[]}
 */
export function pageDestinationOptions(allPages) {
  const pages = Array.isArray(allPages) ? allPages : [];
  return pages.map((page, index) => ({
    id: page.id,
    label: index === 0 ? `${page.name} (Home)` : page.name,
    href: pageHref(page, pages)
  }));
}
